import { router } from "expo-router";
import React, { useMemo, useRef, useState } from "react";
import { View } from "react-native";
import Animated, { useSharedValue } from "react-native-reanimated";
import { Display, mq } from "react-native-unistyles";

import Button, { ButtonGroup } from "$src/core/components/atoms/Button/Button";
import Icon from "$src/core/components/atoms/Icon";
import type { ModalRef } from "$src/core/components/atoms/Modal/types";
import Pressable from "$src/core/components/atoms/Pressable";
import Text from "$src/core/components/atoms/Text/Text";
import EpisodeCard from "$src/core/components/molecules/EpisodeCard/EpisodeCard";
import SeasonSelectorModal from "$src/core/components/molecules/SeasonSelectorModal/SeasonSelectorModal";
import useTranslation from "$src/core/hooks/use-translation";
import type { EpisodeContentDto, SeasonDto } from "$src/core/models/dto";

import $, { useAnimatedStyles } from "./EpisodeBrowser.styles";

const EpisodeCardButton = ({ episode }: { episode: EpisodeContentDto }) => {
  const isFocused = useSharedValue(false);

  const { episodeCardStyle } = useAnimatedStyles({ isFocused });

  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => router.navigate(`/episodes/${episode.id}`)}
      onFocus={() => isFocused.set(true)}
      onBlur={() => isFocused.set(false)}
    >
      <Animated.View style={[$.episodeCard, episodeCardStyle]}>
        <EpisodeCard
          title={episode.title}
          description={episode.shortDescription}
          image={episode.image ?? episode.images}
        />
      </Animated.View>
    </Pressable>
  );
};

const EpisodeBrowser = ({ seasons }: { seasons: SeasonDto[] }) => {
  const { t } = useTranslation();

  const bottomSheetModalRef = useRef<ModalRef | null>(null);
  const firstSeasonRef = useRef<View | null>(null);

  const visibleSeasons = seasons.filter((season) => season.episodes?.length);

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [_selectedSeasonId, setSelectedSeasonId] = useState<string | null>(
    null,
  );

  const handleSelectSeason = (seasonId: string) => {
    setSelectedSeasonId(seasonId);
    bottomSheetModalRef.current?.dismiss();
  };

  const selectedSeasonId =
    _selectedSeasonId ?? visibleSeasons.at(0)?.id ?? null;

  const selectedSeason = seasons.find(
    (season) => season.id === selectedSeasonId,
  );
  const selectedSeasonTitle =
    selectedSeason?.title ||
    t("DETAIL/SEASON", { number: selectedSeason?.number ?? 1 });

  const selectedEpisodes = selectedSeason?.episodes ?? [];

  const Seasons = useMemo(
    () => (
      <ButtonGroup direction="column" style={$.seasons}>
        {visibleSeasons.map((season, i) => {
          const isSelected = selectedSeasonId === season.id;
          const label =
            season.title ||
            t("DETAIL/SEASON", { number: season.number ?? i + 1 });

          return (
            <Button
              key={season.id}
              ref={i === 0 ? firstSeasonRef : null}
              name="episode_browser_season"
              preset={isSelected ? "primary" : "transparent"}
              align="left"
              label={label}
              onPress={() => setSelectedSeasonId(season.id)}
            />
          );
        })}
      </ButtonGroup>
    ),
    [visibleSeasons, selectedSeasonId, t, setSelectedSeasonId],
  );

  return (
    <>
      {visibleSeasons.length && (
        <View>
          <View style={$.header}>
            <Display mq={mq.only.width("lg")}>
              <Text accessibilityRole="header" preset="heading2">
                {t("COMMON/EPISODES")}
              </Text>
            </Display>

            <Display mq={mq.only.width(undefined, "lg")}>
              <Pressable
                style={$.seasonSelector}
                onPress={() => bottomSheetModalRef.current?.present()}
                accessibilityRole="button"
                focusable
              >
                <Text preset="heading4" accessibilityRole="text">
                  {selectedSeasonTitle}
                </Text>
                <Icon name="chevron-down" style={$.icon} />
              </Pressable>
            </Display>
          </View>

          <View style={$.container}>
            {!!visibleSeasons.length && (
              <Display mq={mq.only.width("lg")}>{Seasons}</Display>
            )}

            <View style={$.episodes}>
              {selectedEpisodes.map((episode) => (
                <EpisodeCardButton key={episode.id} episode={episode} />
              ))}
            </View>
          </View>
        </View>
      )}

      <SeasonSelectorModal
        ref={bottomSheetModalRef}
        seasons={visibleSeasons}
        selectedSeasonId={selectedSeasonId}
        onSelect={handleSelectSeason}
      />
    </>
  );
};

export default EpisodeBrowser;
